// Small filter Selects shared by every report screen's filter bar (goal text:
// "A shared report-table + filter-bar component..."). v1 tenants carry small
// masters (sites/locations/products/suppliers/customers/cost-centres in the
// tens), so a plain full-list `Select` is enough — no typeahead search, mirroring
// stores-transfers/components/SiteSelect.tsx's own documented reasoning.
import { useMemo } from 'react';
import { Select, type SelectProps } from '@mantine/core';
import {
  useCostCentres,
  useCustomers,
  useLocations,
  useProcessStages,
  useProducts,
  useSites,
  useSuppliers,
} from '../api';

type BaseSelectProps = Omit<SelectProps, 'data' | 'value' | 'onChange' | 'searchable'>;

interface FilterSelectProps extends BaseSelectProps {
  value: string | null;
  onChange: (value: string | null) => void;
}

export function SiteFilterSelect({ value, onChange, ...rest }: FilterSelectProps) {
  const { data, isLoading } = useSites();
  const options = useMemo(() => (data?.items ?? []).map((s) => ({ value: s.id, label: `${s.code} — ${s.name}` })), [data]);
  return (
    <Select
      {...rest}
      placeholder={rest.placeholder ?? 'All sites'}
      clearable
      data={options}
      value={value}
      onChange={onChange}
      nothingFoundMessage={isLoading ? 'Loading…' : 'No sites found'}
      comboboxProps={{ withinPortal: true }}
    />
  );
}

interface LocationFilterSelectProps extends FilterSelectProps {
  siteId?: string;
}

export function LocationFilterSelect({ value, onChange, siteId, ...rest }: LocationFilterSelectProps) {
  const { data, isLoading } = useLocations({ siteId });
  const options = useMemo(() => (data?.items ?? []).map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` })), [data]);
  return (
    <Select
      {...rest}
      placeholder={rest.placeholder ?? 'All locations'}
      clearable
      data={options}
      value={value}
      onChange={onChange}
      nothingFoundMessage={isLoading ? 'Loading…' : 'No locations found'}
      comboboxProps={{ withinPortal: true }}
    />
  );
}

export function ProductFilterSelect({ value, onChange, ...rest }: FilterSelectProps) {
  const { data, isLoading } = useProducts({ isActive: true });
  const options = useMemo(() => (data?.items ?? []).map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` })), [data]);
  return (
    <Select
      {...rest}
      placeholder={rest.placeholder ?? 'All products'}
      clearable
      searchable
      data={options}
      value={value}
      onChange={onChange}
      nothingFoundMessage={isLoading ? 'Loading…' : 'No products found'}
      comboboxProps={{ withinPortal: true }}
    />
  );
}

export function SupplierFilterSelect({ value, onChange, ...rest }: FilterSelectProps) {
  const { data, isLoading } = useSuppliers({ isActive: true });
  const options = useMemo(() => (data?.items ?? []).map((s) => ({ value: s.id, label: `${s.code} — ${s.name}` })), [data]);
  return (
    <Select
      {...rest}
      placeholder={rest.placeholder ?? 'All suppliers'}
      clearable
      searchable
      data={options}
      value={value}
      onChange={onChange}
      nothingFoundMessage={isLoading ? 'Loading…' : 'No suppliers found'}
      comboboxProps={{ withinPortal: true }}
    />
  );
}

export function CustomerFilterSelect({ value, onChange, ...rest }: FilterSelectProps) {
  const { data, isLoading } = useCustomers({ isActive: true });
  const options = useMemo(() => (data?.items ?? []).map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` })), [data]);
  return (
    <Select
      {...rest}
      placeholder={rest.placeholder ?? 'All customers'}
      clearable
      searchable
      data={options}
      value={value}
      onChange={onChange}
      nothingFoundMessage={isLoading ? 'Loading…' : 'No customers found'}
      comboboxProps={{ withinPortal: true }}
    />
  );
}

interface CostCentreFilterSelectProps extends FilterSelectProps {
  siteId?: string;
}

export function CostCentreFilterSelect({ value, onChange, siteId, ...rest }: CostCentreFilterSelectProps) {
  const { data, isLoading } = useCostCentres({ siteId });
  const options = useMemo(() => (data?.items ?? []).map((cc) => ({ value: cc.id, label: `${cc.code} — ${cc.name}` })), [data]);
  return (
    <Select
      {...rest}
      placeholder={rest.placeholder ?? 'All cost centres'}
      clearable
      data={options}
      value={value}
      onChange={onChange}
      nothingFoundMessage={isLoading ? 'Loading…' : 'No cost centres found'}
      comboboxProps={{ withinPortal: true }}
    />
  );
}

interface StageFilterSelectProps extends FilterSelectProps {
  siteId?: string;
}

export function StageFilterSelect({ value, onChange, siteId, ...rest }: StageFilterSelectProps) {
  const { data, isLoading } = useProcessStages({ siteId });
  const options = useMemo(() => (data?.items ?? []).map((s) => ({ value: s.id, label: s.name })), [data]);
  return (
    <Select
      {...rest}
      placeholder={rest.placeholder ?? 'All stages'}
      clearable
      data={options}
      value={value}
      onChange={onChange}
      nothingFoundMessage={isLoading ? 'Loading…' : 'No stages found'}
      comboboxProps={{ withinPortal: true }}
    />
  );
}
